import { ref, computed } from 'vue';
import { nanoid } from 'nanoid';
import { message } from '@koi-design/uxd-ui';
import {
  getBotInfoApi,
  getResourceListApi,
  getEmbeddingModelListApi
} from '@/api/robot';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import agentKbRetrieval from './agentKnowledgeRetrieval';
import { ensureAgenticRagConfig } from '@/views/botSetting/agenticRagConfig';
import { applyRealtimeVoiceModelResolves } from '@/libs/resolveModelReplacement';
// Shared state - all components using this hook will share the same data
const botInfo = ref({});

const loading = ref(false);
const appendParams = ref({});
const isUpdatingFromApi = ref(false);
/** Same source as AddAgent / Create Agent: `getEmbeddingModelListApi` (embedding_model_options). */
const embeddingLangList = ref([]);
const embeddingLocaleCodes = computed(() =>
  (embeddingLangList.value || [])
    .map(i => i?.value)
    .filter(v => v != null && v !== '')
);

function getEffectiveRetrivalAlg(obj) {
  const raw =
    obj?.retrival_alg ?? obj?.agent_knowledgebase_config?.retrival_alg ?? null;
  if (raw == null) return null;
  const n = Number(raw);
  return agentKbRetrieval.isValidAlg(n) ? n : null;
}

/** Same id resolution as bot settings `knowledgeItemId`; avoids incomplete removal when only `knowledge_base_id` exists. */
export function knowledgeResourceId(item) {
  const v = item?.id ?? item?.knowledge_base_id;
  if (v == null || v === '') return '';
  return String(v);
}

/**
 * Keeps only `settings` rows for knowledge bases still in `knowledge_list` (flat + nested).
 * Prevents orphan rows from being lift-copied after a KB is removed, or a newly bound KB from reusing stale rows for old ids.
 */
export function pruneKnowledgeSettingsToKnowledgeList(obj) {
  if (!obj || typeof obj !== 'object') return;
  const list = Array.isArray(obj.knowledge_list) ? obj.knowledge_list : [];
  const validIds = new Set(
    list.map(knowledgeResourceId).filter(id => id !== '' && id !== 'undefined')
  );

  const filterRows = rows => {
    if (!Array.isArray(rows)) return [];
    return rows.filter(
      r =>
        r &&
        r.knowledge_base_id != null &&
        validIds.has(String(r.knowledge_base_id))
    );
  };

  obj.settings = filterRows(obj.settings);

  const nested = obj.agent_knowledgebase_config;
  if (
    nested &&
    typeof nested === 'object' &&
    !Array.isArray(nested) &&
    Array.isArray(nested.settings)
  ) {
    nested.settings = filterRows(nested.settings);
  }
}

/**
 * Drops `settings` rows (flat + nested `agent_knowledgebase_config.settings`) for the given
 * knowledge base ids. Call when ids are newly bound so `ensureKnowledgeSettingsRowsForBot`
 * does not re-absorb stale rows (e.g. unbind KB A then bind the same KB A again).
 */
export function removeAgentKbSettingsRowsForIds(obj, knowledgeBaseIds) {
  if (!obj || typeof obj !== 'object' || !knowledgeBaseIds?.length) return;
  const drop = new Set(
    knowledgeBaseIds
      .map(id => String(id))
      .filter(id => id !== '' && id !== 'undefined')
  );
  if (!drop.size) return;

  const strip = rows => {
    if (!Array.isArray(rows)) return [];
    return rows.filter(
      r =>
        r &&
        (r.knowledge_base_id == null || !drop.has(String(r.knowledge_base_id)))
    );
  };

  if (Array.isArray(obj.settings)) {
    obj.settings = strip(obj.settings);
  }

  const nested = obj.agent_knowledgebase_config;
  if (
    nested &&
    typeof nested === 'object' &&
    !Array.isArray(nested) &&
    Array.isArray(nested.settings)
  ) {
    nested.settings = strip(nested.settings);
  }
}

/**
 * After all knowledge bases are removed, clears flat retrieval fields so a newly bound KB
 * does not keep old rerank / retrival_alg / etc. Fresh defaults are applied on next bind
 * via `applyDefaultGlobalRetrievalFields` inside `syncAgentKnowledgebaseConfig`.
 */
export function resetFlatKnowledgeRetrievalWhenEmpty(obj) {
  if (!obj || typeof obj !== 'object') return;
  if (Array.isArray(obj.knowledge_list) && obj.knowledge_list.length > 0) {
    return;
  }
  agentKbRetrieval.publicFlatKeys.forEach(k => {
    if (k in obj) {
      delete obj[k];
    }
  });
  obj.settings = [];
  obj.agent_knowledgebase_config = {};
  if (
    obj.meta_retrieve_setting &&
    typeof obj.meta_retrieve_setting === 'object'
  ) {
    obj.meta_retrieve_setting = {
      ...obj.meta_retrieve_setting,
      enable: false
    };
  }
}

/** Seed agent-level retrieval fields from product defaults (not from prior user config). */
function applyDefaultGlobalRetrievalFields(obj) {
  const d = agentKbRetrieval.editFormDefaults;
  obj.retrival_alg = agentKbRetrieval.alg.NORMAL;
  obj.topk = d.max_reference_knowledge_num;
  obj.threshold_rerank = d.correlation_threshold;
  obj.enable_rerank = true;
  obj.rerank_model = d.rerank_model;
  obj.small_to_big = d.small_to_big;
  obj.is_recall_filter = d.is_recall_filter;
  obj.meta_retrieve = false;
  delete obj.recall_filter_x;
  delete obj.recall_filter_y;
  if (
    obj.meta_retrieve_setting &&
    typeof obj.meta_retrieve_setting === 'object'
  ) {
    obj.meta_retrieve_setting = {
      ...obj.meta_retrieve_setting,
      enable: false
    };
  }
}

export function ensureKnowledgeSettingsRowsForBot(obj) {
  if (!obj) return;
  if (!Array.isArray(obj.knowledge_list) || obj.knowledge_list.length === 0) {
    return;
  }
  const alg = getEffectiveRetrivalAlg(obj);
  if (alg == null) return;

  /** Merge flat `settings` with nested `agent_knowledgebase_config.settings` so one side is not stale after only `knowledge_list` changes. */
  const byId = new Map();
  const absorbRows = rows => {
    if (!Array.isArray(rows)) return;
    for (const row of rows) {
      if (row && row.knowledge_base_id != null) {
        const sid = String(row.knowledge_base_id);
        if (!byId.has(sid)) {
          byId.set(sid, { ...row });
        }
      }
    }
  };
  absorbRows(obj.settings);
  absorbRows(obj.agent_knowledgebase_config?.settings);
  // Post-modal save embeds fresh rows on `knowledge_list[].settings`; prefer over stale flat/nested.
  if (Array.isArray(obj.knowledge_list)) {
    for (const kb of obj.knowledge_list) {
      const row = kb?.settings;
      if (!row || row.knowledge_base_id == null) continue;
      byId.set(String(row.knowledge_base_id), { ...row });
    }
  }

  const next = [];
  for (const kb of obj.knowledge_list) {
    const kbId = kb?.id ?? kb?.knowledge_base_id;
    if (kb == null || kbId == null) continue;
    const id = String(kbId);
    const row = byId.get(id);
    if (row) {
      next.push({
        ...row,
        knowledge_base_id: row.knowledge_base_id,
        knowledge_base_name: kb.knowledge_base_name || row.knowledge_base_name
      });
    } else {
      next.push(agentKbRetrieval.buildDefaultSettingsRow(alg, kb));
    }
  }
  obj.settings = next;
  if (obj.retrival_alg == null && alg != null) {
    obj.retrival_alg = alg;
  }
}

/**
 * Robots detail returns retrieval config under `agent_knowledgebase_config` (incl. `settings` per KB).
 * `syncAgentKnowledgebaseConfig` expects flat `settings` / `retrival_alg` / `topk` on `obj`.
 * Without this lift, `hasSettings` is false and the next line would wipe nested config with `{}`.
 */
/** When resource_list overwrites with a shallow `agent_knowledgebase_config` without `settings`, keep robots detail rows. */
function restoreAgentKbSettingsFromRobotsDetail(merged, botData) {
  if (!merged || !botData) return;
  const botKbc = botData.agent_knowledgebase_config;
  const mergedKbc = merged.agent_knowledgebase_config;
  const botDirectConfig = botKbc?.knowledge_direct_resp_config;
  const mergedHasDirectConfig =
    mergedKbc?.knowledge_direct_resp_config ||
    mergedKbc?.hit_reply_mode !== undefined ||
    mergedKbc?.is_miss_fallback !== undefined ||
    mergedKbc?.miss_fallback_message !== undefined;
  if (
    !mergedHasDirectConfig &&
    botDirectConfig &&
    typeof botDirectConfig === 'object' &&
    !Array.isArray(botDirectConfig)
  ) {
    merged.agent_knowledgebase_config = {
      ...(mergedKbc &&
      typeof mergedKbc === 'object' &&
      !Array.isArray(mergedKbc)
        ? mergedKbc
        : {}),
      knowledge_direct_resp_config: { ...botDirectConfig }
    };
  }
  const botSettings = botKbc?.settings;
  const mergedSettings = mergedKbc?.settings;
  if (
    Array.isArray(botSettings) &&
    botSettings.length &&
    (!Array.isArray(mergedSettings) || !mergedSettings.length)
  ) {
    const validIds = new Set(
      (merged.knowledge_list || [])
        .map(knowledgeResourceId)
        .filter(id => id !== '' && id !== 'undefined')
    );
    const filteredSettings = botSettings
      .filter(
        row =>
          row &&
          row.knowledge_base_id != null &&
          validIds.has(String(row.knowledge_base_id))
      )
      .map(row => ({ ...row }));
    if (!filteredSettings.length) {
      return;
    }
    merged.agent_knowledgebase_config = {
      ...(merged.agent_knowledgebase_config &&
      typeof merged.agent_knowledgebase_config === 'object' &&
      !Array.isArray(merged.agent_knowledgebase_config)
        ? merged.agent_knowledgebase_config
        : {}),
      ...botKbc,
      settings: filteredSettings
    };
  }
}

function liftKnowledgeRetrievalFromNestedAgentConfig(obj) {
  if (!obj || typeof obj !== 'object') return;
  const nested = obj.agent_knowledgebase_config;
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) return;

  const flatSettingsMissing =
    !Array.isArray(obj.settings) || !obj.settings.length;
  if (
    flatSettingsMissing &&
    Array.isArray(nested.settings) &&
    nested.settings.length
  ) {
    const validIds = new Set(
      (obj.knowledge_list || [])
        .map(knowledgeResourceId)
        .filter(id => id !== '' && id !== 'undefined')
    );
    obj.settings = nested.settings
      .filter(
        row =>
          row &&
          row.knowledge_base_id != null &&
          validIds.has(String(row.knowledge_base_id))
      )
      .map(row => ({ ...row }));
  }

  // Only fill flat keys when missing. After modal save, flat is newer than nested; overwriting would revert retrival_alg/settings.
  const assignNestedIfFlatMissing = key => {
    if (nested[key] === undefined || nested[key] === null) return;
    if (obj[key] === undefined || obj[key] === null) {
      obj[key] = nested[key];
    }
  };

  assignNestedIfFlatMissing('retrival_alg');
  assignNestedIfFlatMissing('topk');
  assignNestedIfFlatMissing('threshold_rerank');
  assignNestedIfFlatMissing('rerank_model');
  assignNestedIfFlatMissing('enable_rerank');
  assignNestedIfFlatMissing('small_to_big');
  assignNestedIfFlatMissing('meta_retrieve');
  assignNestedIfFlatMissing('is_recall_filter');
  if (nested.is_recall_filter) {
    if (obj.recall_filter_x == null && nested.recall_filter_x != null) {
      obj.recall_filter_x = nested.recall_filter_x;
    }
    if (obj.recall_filter_y == null && nested.recall_filter_y != null) {
      obj.recall_filter_y = nested.recall_filter_y;
    }
  }
}

function buildAgentKnowledgebaseGlobalCfg(obj, settingsRows) {
  const metaRetrieve =
    obj.meta_retrieve !== undefined
      ? obj.meta_retrieve
      : obj.meta_retrieve_setting?.enable;
  const kbc = obj.agent_knowledgebase_config || {};
  const nestedDirectConfig = kbc.knowledge_direct_resp_config;
  const directConfig =
    nestedDirectConfig &&
    typeof nestedDirectConfig === 'object' &&
    !Array.isArray(nestedDirectConfig)
      ? nestedDirectConfig
      : kbc;
  const alg = getEffectiveRetrivalAlg(obj);
  const cfg = {
    retrival_alg: alg,
    settings: (settingsRows || []).map(row => ({ ...row })),
    meta_retrieve: metaRetrieve,
    small_to_big: obj.small_to_big ?? kbc.small_to_big,
    enable_rerank: obj.enable_rerank ?? kbc.enable_rerank ?? true,
    rerank_model: obj.rerank_model ?? kbc.rerank_model,
    threshold_rerank: obj.threshold_rerank ?? kbc.threshold_rerank,
    topk: obj.topk ?? kbc.topk,
    is_recall_filter: Boolean(
      obj.is_recall_filter ?? kbc.is_recall_filter ?? false
    ),
    hit_reply_mode: Number(directConfig.hit_reply_mode) === 2 ? 2 : 1,
    is_miss_fallback: directConfig.is_miss_fallback === true,
    miss_fallback_message:
      typeof directConfig.miss_fallback_message === 'string'
        ? directConfig.miss_fallback_message
        : ''
  };
  if (cfg.is_recall_filter) {
    cfg.recall_filter_x = obj.recall_filter_x ?? kbc.recall_filter_x;
    cfg.recall_filter_y = obj.recall_filter_y ?? kbc.recall_filter_y;
  }
  return cfg;
}

function syncAgentKnowledgebaseConfig(obj) {
  if (!obj) return;
  pruneKnowledgeSettingsToKnowledgeList(obj);
  liftKnowledgeRetrievalFromNestedAgentConfig(obj);

  const hasKnowledgeList =
    Array.isArray(obj.knowledge_list) && obj.knowledge_list.length > 0;

  if (!hasKnowledgeList) {
    obj.agent_knowledgebase_config = {};
    return;
  }

  if (getEffectiveRetrivalAlg(obj) == null) {
    applyDefaultGlobalRetrievalFields(obj);
  }

  ensureKnowledgeSettingsRowsForBot(obj);

  const alg = getEffectiveRetrivalAlg(obj);
  if (alg == null) {
    obj.agent_knowledgebase_config = {};
    return;
  }

  const hasSettings = Array.isArray(obj?.settings) && obj.settings.length > 0;
  if (!hasSettings) {
    obj.agent_knowledgebase_config = {};
    return;
  }
  obj.agent_knowledgebase_config = buildAgentKnowledgebaseGlobalCfg(
    obj,
    obj.settings
  );
}

export function useBotInfo() {
  const store = useStore();
  const route = useRoute();
  const formatBotInfo = data => {
    if (!data) return data;

    const formatted = { ...data };
    formatted.deep_thinking_enable = Boolean(data.deep_thinking_enable);
    formatted.deep_thinking_level = data.deep_thinking_level || '';
    formatted.support_deep_thinking = Boolean(data.support_deep_thinking);
    formatted.deep_thinking_config =
      data.deep_thinking_config &&
      typeof data.deep_thinking_config === 'object' &&
      !Array.isArray(data.deep_thinking_config)
        ? data.deep_thinking_config
        : {};

    // Add nanoid for array items
    formatted.synonym_key_data = data.synonym_key_data.map(i => ({
      ...i,
      id: nanoid()
    }));

    formatted.keyword_data = data.keyword_data.map(i => ({
      ...i,
      id: nanoid()
    }));

    formatted.tip_message_variable = data.tip_message_variable.map(i => {
      let { variable_type } = i;
      if (variable_type && typeof variable_type === 'string') {
        variable_type = variable_type.includes('<')
          ? variable_type.replace('>', '').split('<')
          : [variable_type];
      }
      return {
        ...i,
        variable_type,
        data_id: i.id,
        id: nanoid()
      };
    });

    if (Array.isArray(data.user_memory_variable_content)) {
      formatted.user_memory_variable_content =
        data.user_memory_variable_content.map(i => {
          let { type } = i;
          if (type && typeof type === 'string') {
            type = type.includes('<')
              ? type.replace('>', '').split('<')
              : [type];
          }
          return { ...i, type };
        });
    }

    const m = data.meta_retrieve_setting;
    formatted.meta_retrieve_setting =
      m && typeof m === 'object' && 'enable' in m ? m : { enable: false };

    // Store append params
    appendParams.value = {
      homeland: data.homeland,
      creator: data.creator
    };

    return formatted;
  };
  const getResourceList = async () => {
    const res = await getResourceListApi({
      robot_id: route.query.agentId || route.query.id || route.query.robotId,
      homeland_id: store.getters.homelandInfo.homeland
    });
    const kn = res.knowledge ?? {};
    const kbc = kn.agent_knowledgebase_config ?? {};
    const fromKnowledgeBlock = {};
    if (kn.knowledge_retrieval_status !== undefined) {
      fromKnowledgeBlock.knowledge_retrieval_status =
        kn.knowledge_retrieval_status;
    }
    if (kn.is_qa_answer !== undefined) {
      fromKnowledgeBlock.is_qa_answer = kn.is_qa_answer;
    }
    if (kn.qa_min_threshold !== undefined) {
      fromKnowledgeBlock.qa_min_threshold = kn.qa_min_threshold;
    }
    if (kn.qa_answer_num !== undefined) {
      fromKnowledgeBlock.qa_answer_num = kn.qa_answer_num;
    }
    const base = {
      chatflow_list: res.chatflow?.chatflow_list,
      allow_update_embedding: res.chatflow?.allow_update_embedding,
      plugin_list: res.plugin?.plugin_list,
      tool_list: res.plugin?.tool_list,
      database_list: res.database?.database_list,
      skill_list: res.skill?.skill_list,
      skill_enable: res.skill?.skill_enable ?? false,
      knowledge_list: kn.knowledge_list,
      intention_list: res.intention?.intention_list,
      ...fromKnowledgeBlock,
      ...kbc
    };
    if (Object.keys(kbc).length) {
      base.agent_knowledgebase_config = { ...kbc };
    }
    return base;
  };

  const getBotInfo = async () => {
    const data = await getBotInfoApi({
      id: route.query.agentId || route.query.id || route.query.robotId,
      message_source: 'web_preview'
    });

    const formattedData = formatBotInfo(data);
    delete formattedData.knowledge_list;
    return formattedData;
  };

  const initBotInfo = async () => {
    isUpdatingFromApi.value = true;
    loading.value = true;
    try {
      const homelandId = store.getters.homelandInfo.homeland;
      const embeddingPromise = homelandId
        ? getEmbeddingModelListApi({ homeland_id: homelandId }).catch(() => [])
        : Promise.resolve([]);
      const [botData, resourceData, embList] = await Promise.all([
        getBotInfo(),
        getResourceList(),
        embeddingPromise
      ]);
      embeddingLangList.value = Array.isArray(embList) ? embList : [];
      console.log('botData', botData);
      console.log('resourceData', resourceData);
      // Assign all data after both APIs return
      setDefaultBotData(botData);
      const merged = {
        ...botData,
        ...resourceData,
        fallback_response_message: botData.fallback_response_message ?? '',
        fallback_response_config_enable:
          botData.fallback_response_config_enable ?? false
      };
      if (merged.meta_retrieve !== undefined) {
        merged.meta_retrieve_setting = {
          ...merged.meta_retrieve_setting,
          enable: merged.meta_retrieve
        };
      } else if (merged.meta_retrieve_setting?.enable !== undefined) {
        merged.meta_retrieve = merged.meta_retrieve_setting.enable;
      }
      restoreAgentKbSettingsFromRobotsDetail(merged, botData);
      pruneKnowledgeSettingsToKnowledgeList(merged);
      syncAgentKnowledgebaseConfig(merged);
      ensureAgenticRagConfig(merged);
      const voiceReplacements = await applyRealtimeVoiceModelResolves(merged);
      botInfo.value = merged;
      return voiceReplacements;
    } catch (error) {
      message.error(error);
      throw error;
    } finally {
      loading.value = false;
      isUpdatingFromApi.value = false;
    }
  };

  // Parse language options from emb_language and emb_language_name
  const langOptions = computed(() => {
    if (!botInfo.value.emb_language || !botInfo.value.emb_language_name) {
      return [];
    }

    const languages = botInfo.value.emb_language
      .split(',')
      .map(lang => lang.trim());
    const languageNames = botInfo.value.emb_language_name
      .split(',')
      .map(name => name.trim());

    return languages.map((lang, index) => ({
      label: languageNames[index] || lang,
      value: lang
    }));
  });

  const updateBotInfo = async _patch => {
    isUpdatingFromApi.value = true;
    loading.value = true;
    try {
      const homelandId = store.getters.homelandInfo.homeland;
      const embeddingPromise = homelandId
        ? getEmbeddingModelListApi({ homeland_id: homelandId }).catch(() => [])
        : Promise.resolve([]);
      const [botData, resourceData, embList] = await Promise.all([
        getBotInfo(),
        getResourceList(),
        embeddingPromise
      ]);
      embeddingLangList.value = Array.isArray(embList) ? embList : [];
      // Assign all data after both APIs return
      setDefaultBotData(botData);
      const newBotInfo = {
        ...botData,
        ...resourceData,
        fallback_response_message: botData.fallback_response_message ?? '',
        fallback_response_config_enable:
          botData.fallback_response_config_enable ?? false
      };
      if (newBotInfo.meta_retrieve !== undefined) {
        newBotInfo.meta_retrieve_setting = {
          ...newBotInfo.meta_retrieve_setting,
          enable: newBotInfo.meta_retrieve
        };
      } else if (newBotInfo.meta_retrieve_setting?.enable !== undefined) {
        newBotInfo.meta_retrieve = newBotInfo.meta_retrieve_setting.enable;
      }
      restoreAgentKbSettingsFromRobotsDetail(newBotInfo, botData);
      pruneKnowledgeSettingsToKnowledgeList(newBotInfo);
      syncAgentKnowledgebaseConfig(newBotInfo);
      ensureAgenticRagConfig(newBotInfo);
      await applyRealtimeVoiceModelResolves(newBotInfo);
      botInfo.value = {
        ...botInfo.value,
        ...newBotInfo
      };
    } catch (error) {
      message.error(error);
      throw error;
    } finally {
      loading.value = false;
      isUpdatingFromApi.value = false;
    }
  };

  return {
    // Shared reactive state - all components share the same refs
    botInfo,
    loading,
    appendParams,
    formatBotInfo,
    initBotInfo,
    updateBotInfo,
    isUpdatingFromApi,
    langOptions,
    embeddingLangList,
    embeddingLocaleCodes,
    syncAgentKnowledgebaseConfig,
    pruneKnowledgeSettingsToKnowledgeList,
    resetFlatKnowledgeRetrievalWhenEmpty,
    knowledgeResourceId,
    removeAgentKbSettingsRowsForIds
  };
}

function setDefaultBotData(botData) {
  if (!botData.companion_voice || typeof botData.companion_voice !== 'object') {
    botData.companion_voice = {};
  }
  if (!botData.risk_control_config) {
    botData.risk_control_config = {
      enabled: false,
      strategy_value: 1
    };
  }
  if (botData.copilot_config.stream_source === undefined) {
    botData.copilot_config.stream_source = 0;
  }
  if (!botData.copilot_config.stream_channels) {
    botData.copilot_config.stream_channels = 1;
  }
  if (botData.copilot_config.similarity_enabled === undefined) {
    botData.copilot_config.similarity_enabled = 0;
  }
  if (!botData.copilot_config.similarity_config) {
    botData.copilot_config.similarity_config = {
      model_code: '',
      threshold: 0.8 * 100,
      timeout: 200
    };
  } else {
    botData.copilot_config.similarity_config.threshold *= 100;
  }
  if (botData.copilot_config.emotion_enabled === undefined) {
    botData.copilot_config.emotion_enabled = 0;
  }
  return botData;
}
